import styles from "./ProgressBar.module.css";
import { cx } from "./cx.js";

interface ProgressBarProps {
  /** How much of it has been used. */
  value: number;
  /** The line it is measured against. */
  target: number;
  className?: string;
}

/**
 * Progress against a target: one hue for the bar, a rule line for the target
 * (docs/04 §4.5). Two competing colours make the comparison a colour-matching
 * exercise; a line makes it a glance.
 *
 * The track scales to whichever of the two is larger, so going over is visible
 * as the bar passing the line rather than as a bar that has silently stopped
 * at the end.
 *
 * `aria-hidden`, deliberately. This is always drawn beside the figures it
 * summarises, and a bar that also announces itself makes a screen reader say
 * the same thing twice.
 */
export function ProgressBar({ value, target, className }: ProgressBarProps) {
  const scale = Math.max(value, target, 1);
  const over = target > 0 && value > target;

  return (
    <span className={cx(styles.track, className)} aria-hidden="true">
      <span
        className={cx(styles.bar, over && styles.over)}
        style={{ width: `${String(Math.round((value / scale) * 100))}%` }}
      />
      {target > 0 && (
        <span
          className={styles.rule}
          style={{ left: `${String(Math.round((target / scale) * 100))}%` }}
        />
      )}
    </span>
  );
}
